<template>
  <aside
    class="bg-surface-0 dark:bg-surface-900 flex flex-col"
    data-test-id="merchant-recent-cases"
  >
    <header
      class="flex items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-surface-200/70 dark:border-surface-700/70"
    >
      <div class="flex items-baseline gap-3 min-w-0">
        <h4
          class="text-sm font-semibold text-surface-700 dark:text-surface-200 uppercase tracking-wide"
        >
          Recent Cases
        </h4>
        <span
          v-if="totalCases > 0"
          class="text-[11px] text-surface-500 dark:text-surface-400 tabular-nums"
        >
          {{ recentCases.length }} of {{ totalCases }}
        </span>
        <span
          v-if="openCount > 0"
          class="hidden sm:inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300"
        >
          <span class="w-1 h-1 rounded-full bg-amber-500" />
          {{ openCount }} open
        </span>
      </div>
      <Button
        label="View all"
        icon="pi pi-arrow-right"
        iconPos="right"
        size="small"
        variant="text"
        @click="$emit('switch-tab', 'cases')"
        data-test-id="overview-view-all-cases-btn"
      />
    </header>

    <ul v-if="recentCases.length > 0" class="flex flex-col gap-2 p-3">
      <li
        v-for="caseItem in recentCases"
        :key="caseItem.CASE_ID"
        class="group relative border border-surface-200 dark:border-surface-700/70 rounded-md bg-surface-0 dark:bg-surface-900 hover:border-surface-300 dark:hover:border-surface-600 hover:shadow-sm transition-all"
        :data-test-id="`recent-case-${caseItem.CASE_ID}`"
      >
        <div
          class="flex flex-col gap-2.5 p-3 min-[1440px]:flex-row min-[1440px]:items-center min-[1440px]:gap-4"
        >
          <!-- Identity block: status, case#, jira, age -->
          <div
            class="flex items-center gap-2.5 min-w-0 min-[1440px]:flex-shrink-0 min-[1440px]:w-[300px]"
          >
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded"
              :class="getStatusClasses(caseItem.JIRA_ISSUE?.STATUS)"
            >
              <span
                class="w-1 h-1 rounded-full"
                :class="getStatusDotClass(caseItem.JIRA_ISSUE?.STATUS)"
              />
              {{ caseItem.JIRA_ISSUE?.STATUS || 'Unknown' }}
            </span>
            <button
              type="button"
              class="text-sm font-semibold text-surface-900 dark:text-surface-0 truncate hover:underline text-left tabular-nums"
              @click.stop="navigateToCaseDetails(caseItem.CASE_ID)"
              :data-test-id="`case-link-${caseItem.CASE_ID}`"
            >
              Case #{{ caseItem.CASE_ID }}
            </button>
            <a
              v-if="caseItem.JIRA_ISSUE?.ISSUE_KEY"
              :href="caseItem.JIRA_ISSUE.ISSUE_URL || undefined"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-1 text-[11px] font-mono text-surface-500 dark:text-surface-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              @click.stop
            >
              {{ caseItem.JIRA_ISSUE.ISSUE_KEY }}
              <i class="pi pi-external-link text-[9px]" />
            </a>
          </div>

          <!-- Summary -->
          <p
            v-if="caseItem.JIRA_ISSUE?.SUMMARY"
            class="text-xs text-surface-600 dark:text-surface-300 line-clamp-2 min-[1440px]:flex-1 min-[1440px]:line-clamp-1"
            :title="caseItem.JIRA_ISSUE.SUMMARY"
          >
            {{ caseItem.JIRA_ISSUE.SUMMARY }}
          </p>

          <!-- Meta strip -->
          <div
            class="flex items-center justify-between gap-3 text-[11px] text-surface-500 dark:text-surface-400 min-[1440px]:flex-shrink-0 min-[1440px]:justify-end min-[1440px]:gap-5"
          >
            <span class="inline-flex items-center gap-1.5 truncate">
              <i
                class="pi pi-user text-[10px]"
                :class="
                  caseItem.JIRA_ISSUE?.ASSIGNEE_NAME
                    ? 'text-surface-500 dark:text-surface-400'
                    : 'text-surface-300 dark:text-surface-600'
                "
              />
              <span
                :class="
                  caseItem.JIRA_ISSUE?.ASSIGNEE_NAME
                    ? 'text-surface-700 dark:text-surface-200'
                    : 'italic'
                "
              >
                {{ caseItem.JIRA_ISSUE?.ASSIGNEE_NAME || 'Unassigned' }}
              </span>
            </span>
            <span
              class="inline-flex items-center gap-1 tabular-nums whitespace-nowrap"
              :class="
                ageInfo(caseItem).tone === 'warn'
                  ? 'text-amber-700 dark:text-amber-300 font-medium'
                  : ''
              "
              :title="`Created ${formatDate(caseItem.CREATED_AT)}`"
            >
              <i class="pi pi-clock text-[10px]" />
              {{ ageInfo(caseItem).label }}
            </span>
          </div>

          <!-- Kebab -->
          <div
            class="absolute top-2 right-2 min-[1440px]:static min-[1440px]:ml-1"
          >
            <Button
              icon="pi pi-ellipsis-v"
              rounded
              text
              size="small"
              class="!w-7 !h-7 opacity-50 group-hover:opacity-100 transition-opacity"
              aria-label="Case actions"
              @click.stop="toggleRowMenu($event, caseItem)"
              :data-test-id="`case-actions-btn-${caseItem.CASE_ID}`"
            />
          </div>
        </div>
      </li>
    </ul>

    <div
      v-else
      class="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center"
    >
      <div
        class="w-10 h-10 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center"
      >
        <i
          class="pi pi-folder-open text-lg text-surface-400 dark:text-surface-500"
        />
      </div>
      <p class="text-sm font-medium text-surface-700 dark:text-surface-200">
        No cases yet.
      </p>
      <p class="text-xs text-surface-500 dark:text-surface-400 max-w-[260px]">
        Cases open when an alert is actioned for review.
      </p>
    </div>

    <TieredMenu
      ref="rowMenu"
      id="merchant_case_sidebar_menu"
      :model="rowMenuItems"
      :popup="true"
      class="text-sm"
      :key="rowMenuRowSelected"
    />
  </aside>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { Button } from 'primevue'
import TieredMenu from 'primevue/tieredmenu'
import { useRouter } from 'vue-router'
import { useFCCCaseStore } from '@/stores/fcc/fccCaseStore'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import type { Case } from '@/types/fcc'

interface Props {
  merchantId?: number
}

const props = defineProps<Props>()
const RECENT_CASES_LIMIT = 10
const STALE_DAYS_THRESHOLD = 7

defineEmits<{
  'switch-tab': [tab: string]
}>()

const router = useRouter()
const caseStore = useFCCCaseStore()
const { formatDate } = useFCCFormatting()

const merchantCases = computed((): Case[] => {
  if (!props.merchantId) return []
  return caseStore.cases.filter(c => c.MERCHANT_ID === props.merchantId)
})

const sortedCases = computed((): Case[] =>
  [...merchantCases.value].sort(
    (a, b) =>
      new Date(b.CREATED_AT).getTime() - new Date(a.CREATED_AT).getTime(),
  ),
)
const totalCases = computed(() => merchantCases.value.length)
const recentCases = computed(() =>
  sortedCases.value.slice(0, RECENT_CASES_LIMIT),
)
const openCount = computed(
  () =>
    merchantCases.value.filter(
      c => c.JIRA_ISSUE?.IS_OPEN !== false && !c.RESOLVED_AT,
    ).length,
)

interface StatusTone {
  bg: string
  dot: string
}

const STATUS_TONES: Record<string, StatusTone> = {
  open: {
    bg: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  'in progress': {
    bg: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  resolved: {
    bg: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300',
    dot: 'bg-green-500',
  },
  done: {
    bg: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300',
    dot: 'bg-green-500',
  },
  closed: {
    bg: 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300',
    dot: 'bg-surface-400',
  },
  backlog: {
    bg: 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300',
    dot: 'bg-surface-400',
  },
}
const FALLBACK_TONE: StatusTone = {
  bg: 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300',
  dot: 'bg-surface-400',
}

function tone(status: string | undefined): StatusTone {
  if (!status) return FALLBACK_TONE
  return STATUS_TONES[status.toLowerCase()] ?? FALLBACK_TONE
}
function getStatusClasses(status: string | undefined): string {
  return tone(status).bg
}
function getStatusDotClass(status: string | undefined): string {
  return tone(status).dot
}

function ageInfo(caseItem: Case): { label: string; tone: 'normal' | 'warn' } {
  const start = new Date(caseItem.CREATED_AT).getTime()
  const resolvedAt = caseItem.RESOLVED_AT
    ? new Date(caseItem.RESOLVED_AT).getTime()
    : null
  const end = resolvedAt ?? Date.now()
  const days = Math.max(0, Math.floor((end - start) / 86400000))
  if (resolvedAt) {
    return {
      label: days === 0 ? 'Resolved same day' : `Resolved in ${days}d`,
      tone: 'normal',
    }
  }
  if (days === 0) return { label: 'Opened today', tone: 'normal' }
  if (days === 1) return { label: 'Opened 1d ago', tone: 'normal' }
  return {
    label: `Opened ${days}d ago`,
    tone: days >= STALE_DAYS_THRESHOLD ? 'warn' : 'normal',
  }
}

interface MenuItem {
  label: string
  icon: string
  command: () => void
}

const rowMenu = ref<InstanceType<typeof TieredMenu> | null>(null)
const rowMenuItems = ref<MenuItem[]>([])
const rowMenuRowSelected = ref<number | undefined>(undefined)

async function toggleRowMenu(event: Event, caseItem: Case) {
  rowMenuRowSelected.value = caseItem.CASE_ID
  rowMenuItems.value = buildRowMenuItems(caseItem)
  await nextTick()
  rowMenu.value?.toggle(event)
}

function buildRowMenuItems(caseItem: Case): MenuItem[] {
  const items: MenuItem[] = [
    {
      label: 'View Case',
      icon: 'pi pi-briefcase',
      command: () => navigateToCaseDetails(caseItem.CASE_ID),
    },
  ]
  if (caseItem.JIRA_ISSUE?.ISSUE_URL) {
    items.push({
      label: 'Open JIRA Ticket',
      icon: 'pi pi-external-link',
      command: () => window.open(caseItem.JIRA_ISSUE.ISSUE_URL, '_blank'),
    })
  }
  return items
}

function navigateToCaseDetails(caseId: number) {
  router.push({ name: 'fccCaseDetails', params: { id: caseId.toString() } })
}
</script>
